/**
 * Topics Overview Component
 * Displays framework topics with coverage breakdown and progress
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Circle, ChevronRight } from 'lucide-react';

export default function TopicsOverview({ framework, requirements, coverage }) {
  // Group requirements by topic
  const topicGroups = getTopicGroups(framework, requirements, coverage);

  return (
    <div className="space-y-6" data-testid="topics-overview">
      {topicGroups.map(topic => (
        <div 
          key={topic.topicCode}
          className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl overflow-hidden"
        >
          {/* Topic Header */}
          <div className="p-6 border-b border-[#1A1A1A]">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-white">{topic.topicName}</h3>
                  <span className="px-2 py-0.5 text-xs font-mono bg-[#1A1A1A] text-[#6B6B6B] rounded">
                    {topic.topicCode}
                  </span>
                  {topic.subtopics && topic.subtopics.length > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-[#1A1A1A] text-[#6B6B6B] rounded">
                      {topic.subtopics.length} subtopic{topic.subtopics.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#6B6B6B]">
                  {topic.stats.total} total requirements
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-emerald-400">{topic.stats.percentage}%</p>
                <p className="text-xs text-[#6B6B6B]">complete</p>
              </div>
            </div>
          </div>

          {/* Progress Bar & Stats */}
          <div className="p-6">
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-xs text-[#6B6B6B] mb-2">
                <span>Coverage Progress</span>
                <span>{topic.stats.completed} / {topic.stats.total}</span>
              </div>
              <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${topic.stats.percentage}%` }}
                />
              </div>
            </div>

            {/* Stats Breakdown */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 size={16} className="text-emerald-400" />
                  <span className="text-xs font-medium text-emerald-400">Complete</span>
                </div>
                <p className="text-xl font-bold text-emerald-400">{topic.stats.completed}</p>
              </div>

              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={16} className="text-amber-400" />
                  <span className="text-xs font-medium text-amber-400">Partial</span>
                </div>
                <p className="text-xl font-bold text-amber-400">{topic.stats.partial}</p>
              </div>

              <div className="p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="flex items-center gap-2 mb-1">
                  <Circle size={16} className="text-[#6B6B6B]" />
                  <span className="text-xs font-medium text-[#6B6B6B]">Missing</span>
                </div>
                <p className="text-xl font-bold text-[#6B6B6B]">{topic.stats.missing}</p>
              </div>
            </div>

            {/* Subtopics */}
            {topic.subtopics && topic.subtopics.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-white mb-3">Subtopics:</p>
                <div className="space-y-2">
                  {topic.subtopics.map(subtopic => (
                    <Link
                      key={subtopic.code}
                      to={`/frameworks/csrd-esrs?requirement=${subtopic.id}`}
                      className="flex items-center justify-between p-3 rounded-lg border border-[#1A1A1A] hover:border-emerald-500/30 hover:bg-[#111111] transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-0.5 text-xs font-mono bg-[#1A1A1A] text-[#6B6B6B] rounded">
                          {subtopic.code}
                        </span>
                        <span className="text-sm text-[#A0A0A0] group-hover:text-white transition-colors">
                          {subtopic.name}
                        </span>
                        {subtopic.mandatory && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-blue-500/20 text-blue-400 rounded">
                            Required
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#6B6B6B]">
                          {subtopic.completed}/{subtopic.total}
                        </span>
                        <div className="w-16 h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full"
                            style={{ width: `${subtopic.percentage}%` }}
                          />
                        </div>
                        <ChevronRight size={16} className="text-[#4A4A4A] group-hover:text-emerald-400 transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper function to build topic groups
function getTopicGroups(framework, requirements, coverage) {
  const topics = framework?.topics || [];
  
  // Map topic codes to requirements
  const topicMap = {};
  
  requirements.forEach(req => {
    // Extract base topic code (e.g., "E1" from "E1-5" or "E1-5.2")
    const code = req.requirementCode;
    let baseCode = code;
    
    // Match pattern like E1, S1, G1, ESRS_1, etc.
    const match = code.match(/^([A-Z]+[0-9]?|ESRS_[0-9]+)/);
    if (match) {
      baseCode = match[1];
    }
    
    if (!topicMap[baseCode]) {
      topicMap[baseCode] = {
        requirements: [],
        subtopics: {}
      };
    }
    
    topicMap[baseCode].requirements.push(req);
    
    // Track subtopics (e.g., E1-1, E1-2)
    const subtopicMatch = code.match(/^([A-Z]+[0-9]+-[0-9]+)/);
    if (subtopicMatch) {
      const subtopicCode = subtopicMatch[1];
      if (!topicMap[baseCode].subtopics[subtopicCode]) {
        topicMap[baseCode].subtopics[subtopicCode] = {
          requirements: []
        };
      }
      topicMap[baseCode].subtopics[subtopicCode].requirements.push(req);
    }
  });

  return topics.map(topic => {
    const topicData = topicMap[topic.topic_code] || { requirements: [], subtopics: {} };
    const topicReqs = topicData.requirements;
    
    // Calculate stats
    let completed = 0;
    let partial = 0;
    
    topicReqs.forEach(req => {
      const reqCoverage = coverage?.requirementsCoverage?.[req.id];
      if (reqCoverage?.status === 'complete') completed++;
      else if (reqCoverage?.status === 'partial') partial++;
    });

    const missing = topicReqs.length - completed - partial;
    const percentage = topicReqs.length > 0 
      ? Math.round((completed / topicReqs.length) * 100) 
      : 0;

    // Build subtopics array
    const subtopics = Object.entries(topicData.subtopics).map(([code, data]) => {
      const subtopicReqs = data.requirements;
      const subtopicCompleted = subtopicReqs.filter(r => 
        coverage?.requirementsCoverage?.[r.id]?.status === 'complete'
      ).length;
      const subtopicPercentage = subtopicReqs.length > 0
        ? Math.round((subtopicCompleted / subtopicReqs.length) * 100)
        : 0;

      // Find the parent requirement for this subtopic
      const parentReq = requirements.find(r => r.requirementCode === code);

      return {
        id: parentReq?.id || code,
        code,
        name: parentReq?.requirementTitle || code,
        mandatory: parentReq?.isMandatory ?? true,
        total: subtopicReqs.length,
        completed: subtopicCompleted,
        percentage: subtopicPercentage
      };
    }).sort((a, b) => a.code.localeCompare(b.code));

    return {
      topicCode: topic.topic_code,
      topicName: topic.topic_name,
      stats: {
        total: topicReqs.length,
        completed,
        partial,
        missing,
        percentage
      },
      subtopics
    };
  });
}
